import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface Redirect {
  id: string;
  from_path: string;
  to_path: string;
  is_permanent: boolean;
  is_active: boolean;
  created_at: string;
  updated_by: string | null;
}

export function useRedirects() {
  return useQuery({
    queryKey: ["redirects"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("redirects")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data as Redirect[];
    },
    staleTime: 30_000,
  });
}

export function useActiveRedirects() {
  return useQuery({
    queryKey: ["redirects", "active"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("redirects")
        .select("from_path, to_path, is_permanent")
        .eq("is_active", true);
      if (error) throw error;
      return data as Pick<Redirect, "from_path" | "to_path" | "is_permanent">[];
    },
    staleTime: 60_000,
  });
}

export function useUpsertRedirect() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (r: Partial<Redirect> & { from_path: string; to_path: string }) => {
      if (r.id) {
        const { error } = await supabase.from("redirects").update(r).eq("id", r.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("redirects").insert(r);
        if (error) throw error;
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["redirects"] }),
  });
}

export function useDeleteRedirect() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("redirects").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["redirects"] }),
  });
}
